import { memo, useEffect, useState } from 'react';
import { Avatar, Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { NumericFormat } from 'react-number-format';
import moment from 'moment';

import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useTopPanel from 'hooks/useTopPanel';
import FormDateField from 'components/ui/form/FormDateField';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Money = ({ sum, valute, zero = false }) => (
  <div className='flex flex-col items-end leading-tight'>
    {sum > 0 && (
      <NumericFormat
        value={sum}
        displayType='text'
        thousandSeparator=' '
        decimalScale={2}
        suffix=' UZS'
      />
    )}
    {valute > 0 && (
      <NumericFormat
        value={valute}
        displayType='text'
        thousandSeparator=' '
        decimalScale={2}
        suffix=' $'
      />
    )}
    {!sum && !valute && <span className='text-gray-400'>{zero ? '0 UZS' : '—'}</span>}
  </div>
);

/**
 * Bir xodim uchun batafsil KPI kesimi — faqat qator kengaytirilganda yuklanadi.
 */
const StaffKpiDetail = memo(({ userId, from, till }) => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();

  const { data: kpi, isFetching } = useQuery({
    queryKey: ['/admin/staff-kpi/staff', userId, from, till],
    queryFn: async () =>
      (
        await axiosPrivate.get(
          `/admin/staff-kpi/staff/${userId}?from=${from}&till=${till}`,
        )
      ).data.data,
    retry: false,
  });

  if (isFetching) {
    return (
      <div className='flex justify-center py-4'>
        <CircularProgress size={20} />
      </div>
    );
  }

  if (!kpi?.items?.length) {
    return (
      <div className='text-center text-gray-400 py-3 text-sm'>
        <i className='bi bi-exclamation-octagon mr-1' />
        {t('common.global.noDataFound')}
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-2 p-3'>
      {kpi.items.map((item, idx) => {
        const sources = item?.sources || [];
        return (
          <div
            key={idx}
            className='rounded-lg border border-gray-200 bg-white overflow-hidden'
          >
            <div className='flex items-center justify-between px-3 py-2 gap-2'>
              <div className='flex flex-col gap-0.5 min-w-0 flex-1'>
                <span className='font-medium text-sm text-gray-800 truncate'>
                  {item?.kpi?.type?.name || `#${item?.kpi?.type_id}`}
                </span>
                <span className='text-xs text-gray-500'>
                  {item?.kpi?.tiers?.length > 0 ? (
                    <>{t('settings.staffKpi.tiers.tiered')} · {item.kpi.tiers.length}</>
                  ) : (
                    <>
                      {item?.kpi?.count_type_text} ·{' '}
                      <NumericFormat
                        value={item?.kpi?.price}
                        displayType='text'
                        thousandSeparator={item?.kpi?.count_type === 2 ? ' ' : ''}
                        decimalScale={5}
                      />
                      {item?.kpi?.count_type === 1 ? '%' : ''}
                    </>
                  )}
                </span>
              </div>
              <Money sum={item?.earned_sum} valute={item?.earned_valute} />
            </div>
            {sources.length > 0 && (
              <div className='border-t border-gray-100 bg-gray-50 px-3 py-2 flex flex-col gap-1'>
                {sources.map((src, sidx) => {
                  const clickable = !!src.contract_id;
                  return (
                    <div
                      key={sidx}
                      onClick={
                        clickable
                          ? () => navigate(`/admin/contract/view/${src.contract_id}`)
                          : undefined
                      }
                      className={`flex items-center justify-between text-xs gap-2 ${
                        clickable
                          ? 'cursor-pointer hover:bg-gray-100 -mx-1 px-1 py-0.5 rounded'
                          : ''
                      }`}
                    >
                      <div className='flex flex-col min-w-0 flex-1'>
                        <span
                          className={`truncate ${
                            clickable ? 'text-blue-600 hover:underline' : 'text-gray-700'
                          }`}
                        >
                          {src.label || `#${src.id}`}
                        </span>
                        <span className='text-gray-400'>{src.event_date || '—'}</span>
                      </div>
                      <div className='font-medium text-gray-800 shrink-0'>
                        <NumericFormat
                          value={src.earned}
                          displayType='text'
                          thousandSeparator=' '
                          decimalScale={2}
                          suffix={src.isvalute === 1 ? ' $' : ' UZS'}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
});

const StaffKpi = () => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const { setComponent } = useTopPanel();

  useEffect(() => {
    setComponent(
      <div className='component-title'>{t('settings.staffKpi.allTitle')}</div>,
    );
  }, [t]);

  const formik = useFormik({
    initialValues: {
      from: moment().startOf('month').format('YYYY-MM-DD'),
      till: moment().endOf('month').format('YYYY-MM-DD'),
    },
    onSubmit: () => {},
  });
  const { from, till } = formik.values;

  const { data, isFetching } = useQuery({
    queryKey: ['/admin/staff-kpi/all', from, till],
    queryFn: async () =>
      (await axiosPrivate.get(`/admin/staff-kpi/all?from=${from}&till=${till}`))
        .data.data,
    retry: false,
    enabled: !!from && !!till,
  });

  const rows = data?.data || [];
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const setThisMonth = () => {
    formik.setFieldValue('from', moment().startOf('month').format('YYYY-MM-DD'));
    formik.setFieldValue('till', moment().endOf('month').format('YYYY-MM-DD'));
  };

  return (
    <div className='component-wrapper p-4'>
      {/* Sana filtri */}
      <div className='bg-white base-border p-4 mb-4 flex flex-wrap items-end gap-3'>
        <div className='min-w-[170px]'>
          <FormDateField
            delay={0}
            label={t('common.table.from')}
            fieldName='from'
            formik={formik}
          />
        </div>
        <div className='min-w-[170px]'>
          <FormDateField
            delay={0}
            label={t('common.table.till')}
            fieldName='till'
            formik={formik}
          />
        </div>
        <Button variant='outlined' size='small' onClick={setThisMonth}>
          {t('boss.payment.monthly')}
        </Button>
      </div>

      {/* Jami kartalar */}
      {data && (
        <div className='grid grid-cols-1 md:grid-cols-2 gap-3 mb-4'>
          <div className='bg-emerald-50 border border-emerald-200 rounded-xl p-4'>
            <div className='text-xs text-emerald-600 mb-1'>
              {t('common.table.total')} (UZS)
            </div>
            <div className='text-lg font-bold text-emerald-700'>
              <NumericFormat
                value={data.grand_total_sum}
                displayType='text'
                thousandSeparator=' '
                decimalScale={2}
                suffix=' UZS'
              />
            </div>
          </div>
          <div className='bg-emerald-50 border border-emerald-200 rounded-xl p-4'>
            <div className='text-xs text-emerald-600 mb-1'>
              {t('common.table.total')} ($)
            </div>
            <div className='text-lg font-bold text-emerald-700'>
              <NumericFormat
                value={data.grand_total_valute}
                displayType='text'
                thousandSeparator=' '
                decimalScale={2}
                suffix=' $'
              />
            </div>
          </div>
        </div>
      )}

      {/* Xodimlar jadvali */}
      <div className='bg-white base-border overflow-hidden'>
        <div className='hidden md:flex items-center px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase border-b border-gray-100'>
          <div className='flex-1'>{t('settings.staffKpi.staff')}</div>
          <div className='w-[110px] text-center'>KPI</div>
          <div className='w-[180px] text-right'>{t('settings.staffKpi.earnings.title')}</div>
          <div className='w-[40px]' />
        </div>

        {isFetching ? (
          <div className='flex justify-center py-16'>
            <CircularProgress size={30} />
          </div>
        ) : rows.length === 0 ? (
          <div className='text-center text-gray-400 py-10 text-sm'>
            <i className='bi bi-exclamation-octagon mr-1' />
            {t('common.global.noDataFound')}
          </div>
        ) : (
          rows.map((row) => {
            const u = row.user || {};
            const isOpen = expanded.has(u.id);
            return (
              <div key={u.id} className='border-b border-gray-100 last:border-b-0'>
                <div
                  onClick={() => toggle(u.id)}
                  className='flex items-center px-4 py-3 gap-2 cursor-pointer hover:bg-gray-50'
                >
                  <div className='flex items-center gap-3 flex-1 min-w-0'>
                    <Avatar
                      alt={u.name}
                      src={
                        u.image
                          ? `${BACKEND_URL}/${String(u.image).replace(/^\/+/, '')}`
                          : undefined
                      }
                      sx={{ width: 40, height: 40 }}
                    />
                    <div className='min-w-0'>
                      <div className='font-medium text-gray-800 truncate'>
                        {u.name}
                      </div>
                      <div className='text-xs text-gray-400 truncate'>
                        {u.role_name || '—'}
                      </div>
                    </div>
                  </div>
                  <div className='w-[110px] text-center text-sm text-gray-600'>
                    {row.kpi_count || 0}
                  </div>
                  <div className='w-[180px] text-right text-sm font-medium text-gray-800'>
                    <Money sum={row.total_sum} valute={row.total_valute} zero />
                  </div>
                  <div className='w-[40px] text-center text-gray-400'>
                    <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'}`} />
                  </div>
                </div>
                {isOpen && (
                  <div className='bg-gray-50 border-t border-gray-100'>
                    <StaffKpiDetail userId={u.id} from={from} till={till} />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default memo(StaffKpi);
